import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Pagination,
  Row,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
} from "antd";
import { PlusOutlined, ReloadOutlined, TagOutlined } from "@ant-design/icons";
import { useThemeMode } from "../../../contexts/ThemeContext";
import { getLocalAntdTheme } from "../../../theme/antdTheme";
import { Wrapper } from "./style";
import {
  createCoupon,
  fetchAvailablePlans,
  fetchCoupons,
} from "./lib/api";

const formatDateTime = (unix) => {
  const ts = parseInt(unix, 10);
  if (!ts) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(ts * 1000));
};

const formatApplicablePlans = (coupon) => {
  const details = coupon?.applicable_plans_details;
  if (Array.isArray(details) && details.length) {
    return details.map((plan) => plan?.name || plan?.id).filter(Boolean);
  }
  const planIds = coupon?.applicable_plans;
  if (Array.isArray(planIds) && planIds.length) {
    return planIds.map(String);
  }
  return [];
};

export default function BillingCoupons({ userData }) {
  const { themeMode } = useThemeMode();
  const token = userData?.token;

  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const loadCoupons = useCallback(
    async (override = {}) => {
      if (!token) return;
      setLoading(true);
      try {
        const data = await fetchCoupons(
          {
            page: override.page ?? page,
            perPage: override.perPage ?? perPage,
          },
          token
        );
        setCoupons(data.coupons);
        setTotal(data.pagination.total || 0);
        setPage(data.pagination.page || override.page || page);
        setPerPage(data.pagination.per_page || override.perPage || perPage);
      } catch (err) {
        message.warning(err?.message || "Failed to load coupons");
        setCoupons([]);
      } finally {
        setLoading(false);
      }
    },
    [token, page, perPage]
  );

  const loadAvailablePlans = useCallback(async () => {
    if (!token) return;
    setPlansLoading(true);
    try {
      const plans = await fetchAvailablePlans(token);
      setAvailablePlans(plans);
    } catch (err) {
      message.warning(err?.message || "Failed to load available plans");
      setAvailablePlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCoupons({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openCreateModal = () => {
    form.resetFields();
    form.setFieldsValue({
      type: "percent",
      is_active: true,
      discount_value: 10,
      max_uses: 100,
      applicable_plans: [],
    });
    setCreateOpen(true);
    loadAvailablePlans();
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    form.resetFields();
  };

  const handleCreateCoupon = async (values) => {
    setCreating(true);
    try {
      const payload = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        type: values.type,
        discount_value: values.discount_value,
        max_uses: values.max_uses,
        is_active: values.is_active ? 1 : 0,
        applicable_plans: values.applicable_plans || [],
      };
      const response = await createCoupon(payload, token);
      message.success(response?.message || "Coupon created successfully");
      closeCreateModal();
      loadCoupons({ page: 1 });
    } catch (err) {
      message.warning(err?.message || "Failed to create coupon");
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (_, __, index) => (page - 1) * perPage + index + 1,
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code) => <span className="billing-coupons-code">{code}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => name || "—",
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, row) => (
        <Tag color="processing" className="billing-coupons-plan-tag">
          {row.formatted_discount || `${row.discount_value}%`}
        </Tag>
      ),
    },
    {
      title: "Usage",
      key: "usage",
      render: (_, row) => {
        const used = row.usage_count ?? 0;
        const limit = row.usage_limit ?? row.max_uses;
        return limit != null ? `${used} / ${limit}` : used;
      },
    },
    {
      title: "Applicable Plans",
      key: "applicable_plans",
      render: (_, row) => {
        const plans = formatApplicablePlans(row);
        if (!plans.length) return <span className="text-muted">All plans</span>;
        return (
          <div className="billing-coupons-plan-tags">
            {plans.map((plan) => (
              <Tag key={plan} className="billing-coupons-plan-tag">
                {plan}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (isActive) =>
        isActive === 1 ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: formatDateTime,
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: formatDateTime,
    },
  ];

  const theme = getLocalAntdTheme(themeMode, "#f45b68");

  const planOptions = useMemo(
    () =>
      availablePlans.map((plan) => ({
        value: plan.id,
        label: plan.name,
        searchText: [plan.name, plan.description]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
        plan,
      })),
    [availablePlans]
  );

  return (
    <Wrapper>
      <Helmet>
        <title>Billing Coupons</title>
      </Helmet>

      <div className="billing-coupons-header">
        <div>
          <div className="billing-coupons-title">Billing Coupons</div>
          <div className="billing-coupons-subtitle">
            Create and manage discount coupons for subscription plans.
          </div>
        </div>
      </div>

      <div className="billing-coupons-toolbar">
        <Button icon={<ReloadOutlined />} onClick={() => loadCoupons()} loading={loading}>
          Refresh
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
          className="fs-7 fw-bold"
          style={{ background: "#f45b68", borderColor: "#f45b68" }}
        >
          Create Coupon
        </Button>
      </div>

      <div className="card card-xxl-stretch mb-5 mb-xl-8">
        <div className="card-body py-3">
          <ConfigProvider theme={theme}>
            <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={coupons}
              pagination={false}
              scroll={{ x: 1100 }}
              className="billing-coupons-table"
              components={{
                header: {
                  cell: (props) => (
                    <th
                      {...props}
                      className={`${props.className || ""} billing-coupons-table-header-row`}
                    />
                  ),
                },
              }}
            />
            {total > 0 && (
              <div className="d-flex justify-content-end mt-4">
                <Pagination
                  current={page}
                  pageSize={perPage}
                  total={total}
                  showSizeChanger
                  pageSizeOptions={[10, 20, 50]}
                  onChange={(nextPage, nextSize) => {
                    setPage(nextPage);
                    setPerPage(nextSize);
                    loadCoupons({ page: nextPage, perPage: nextSize });
                  }}
                />
              </div>
            )}
          </ConfigProvider>
        </div>
      </div>

      <ConfigProvider theme={theme}>
        <Modal
          className="billing-coupons-modal"
          title="Create Coupon"
          open={createOpen}
          onCancel={closeCreateModal}
          width={600}
          centered
          destroyOnClose
          footer={[
            <button
              key="cancel"
              type="button"
              className="btn btn-light fs-7 fw-bold me-2"
              onClick={closeCreateModal}
              disabled={creating}
            >
              Cancel
            </button>,
            <button
              key="create"
              type="button"
              className="btn btn-danger fs-7 fw-bold text-white"
              onClick={() => form.submit()}
              disabled={creating}
            >
              {creating ? "Creating..." : "Create"}
            </button>,
          ]}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateCoupon}
            requiredMark={false}
            className="billing-coupons-form"
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Code"
                  name="code"
                  rules={[
                    { required: true, message: "Enter a coupon code" },
                    {
                      pattern: /^[A-Za-z0-9_-]+$/,
                      message: "Invalid characters in code",
                    },
                  ]}
                >
                  <Input
                    className="billing-coupons-form-input"
                    placeholder="WELCOME10"
                    maxLength={32}
                    prefix={<TagOutlined className="billing-coupons-input-icon" />}
                    onChange={(e) => {
                      form.setFieldValue("code", e.target.value.toUpperCase());
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[{ required: true, message: "Enter a coupon name" }]}
                >
                  <Input
                    className="billing-coupons-form-input"
                    placeholder="Welcome discount"
                    maxLength={120}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="type" hidden>
              <Input />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Discount (%)"
                  name="discount_value"
                  rules={[
                    { required: true, message: "Enter discount" },
                    {
                      type: "number",
                      min: 1,
                      max: 100,
                      message: "Must be 1–100",
                    },
                  ]}
                >
                  <InputNumber
                    className="billing-coupons-form-input"
                    min={1}
                    max={100}
                    placeholder="10"
                    addonAfter="%"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Max uses"
                  name="max_uses"
                  rules={[
                    { required: true, message: "Enter max uses" },
                    {
                      type: "number",
                      min: 1,
                      message: "Must be at least 1",
                    },
                  ]}
                >
                  <InputNumber
                    className="billing-coupons-form-input"
                    min={1}
                    placeholder="100"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Plans"
              name="applicable_plans"
              rules={[
                {
                  validator: (_, value) =>
                    Array.isArray(value) && value.length
                      ? Promise.resolve()
                      : Promise.reject(new Error("Select at least one plan")),
                },
              ]}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Select plans"
                loading={plansLoading}
                optionLabelProp="label"
                maxTagCount="responsive"
                className="billing-coupons-plans-select"
                popupClassName="billing-coupons-plans-dropdown"
                filterOption={(input, option) =>
                  (option?.searchText || "").includes(input.toLowerCase())
                }
                notFoundContent={
                  plansLoading ? <Spin size="small" /> : "No plans found"
                }
                options={planOptions.map(({ value, label, searchText }) => ({
                  value,
                  label,
                  searchText,
                }))}
              />
            </Form.Item>

            <Form.Item label="Active" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      </ConfigProvider>
    </Wrapper>
  );
}
